import {
    Button, Checkbox, Form, Input, InputNumber,
} from 'antd';
import {FormInstance} from 'antd/es/form';
import {FormListFieldData} from 'antd/es/form/FormList';
import cn from 'classnames';
import React, {useState, FC} from 'react';

import {DateInput} from 'components/@common/inputs/date-input';
import {DateRangeInput} from 'components/@common/inputs/date-range-input';
import {CustomSelect, ECustomSelectMode} from 'components/form/inputs/custom-select';
import {ECustomSelectValueTriggerEvent} from 'components/form/inputs/custom-select/custom-select';
import {LargeStringInput} from 'components/form/inputs/large-string-input';
import {fieldValidationResolver, EMaskValidation} from 'components/form/utils/field-validation-resolver';
import {useEnableDynamicSql} from 'components/report-configuration/hooks/use-enable-dynamic-sql';
import {EWatcherFieldFlag} from 'components/report-configuration/hooks/use-form-fields-manager';
import {EFieldType} from 'entities/metadata';
import {ReactComponent as TrashXOutlined} from 'shared/assets/trash-x.svg';
import {useAppSelector} from 'store/config/hooks';
import {
    selectReportConfigurationTableAttributesAsSelectEntries,
} from 'store/slices/report-configuration-slice/report-configuration-slice';

import {
    FormatLookupSublistCodesMap,
    REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY,
    IReportPropertiesSettingsDto,
    EReportPropertiesSettingsFormFields,
} from '../constants/report-properties-settings';
import {resetReportPropertiesSettingsFormFields} from '../utils/report-properties-settings.utils';
import s from './report-properties-setting-fields.module.less';

export interface IReportPropertiesSettingFieldsProps {
    form: FormInstance;
    fields: FormListFieldData[];
    remove: (index: (number | number[])) => void;
    checkWatcherFlag: (index: number, key: string, flag: EWatcherFieldFlag) => boolean;
    checkArrayIndependentWatcherFlag: (fieldKey: string, flag: EWatcherFieldFlag) => boolean;
    applyFieldWatchers: (changedVales?: (IReportPropertiesSettingsDto | undefined)) => void;
    values: Record<EReportPropertiesSettingsFormFields, string | boolean>[];
}

export const ReportPropertiesSettingFields: FC<IReportPropertiesSettingFieldsProps> = ({
    form,
    fields,
    remove,
    checkWatcherFlag,
    applyFieldWatchers,
    checkArrayIndependentWatcherFlag,
    values,
}: IReportPropertiesSettingFieldsProps) => {
    const [selectedLookupTypes, setSelectedLookupTypes] = useState<Record<string, string>>({});
    const [formatSublistCodes, setFormatSublistCodes] = useState<Record<string, string>>({});

    const tableAttributesEntries = useAppSelector(selectReportConfigurationTableAttributesAsSelectEntries);

    const isDynamicSqlEnabled = useEnableDynamicSql();

    return (
        <div className={cn('form-list')}>
            {fields.map(field => (
                <div
                    key={field.key}
                    className={cn('form-list__items')}
                >
                    {!checkWatcherFlag(
                        field.name, EReportPropertiesSettingsFormFields.paramName, EWatcherFieldFlag.isHidden,
                    ) && (
                        <Form.Item
                            style={{width: 220}}
                            label="Имя параметра"
                            required={checkWatcherFlag(field.name, EReportPropertiesSettingsFormFields.paramName,
                                EWatcherFieldFlag.isRequired)}
                            name={[field.name, EReportPropertiesSettingsFormFields.paramName]}
                            rules={fieldValidationResolver({
                                validationRules: [{
                                    regExp: '^.{0,500}$',
                                    errorMessage: 'Длина данного поля должна быть не более 500 символов',
                                }, {
                                    regExp: '^[\\w]*$',
                                    errorMessage: 'Поле должно содержать только латинские символы',
                                }, {
                                    mask: EMaskValidation.checkFieldUniqueness,
                                    errorMessage: 'Такое имя параметра уже существует',
                                }],
                                settings: {
                                    checkFieldUniquenessArrayPath: REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY,
                                    checkFieldUniquenessFieldKey: EReportPropertiesSettingsFormFields.paramName,
                                },
                                fieldMeta: {
                                    type: EFieldType.STRING,
                                    label: 'Имя параметра',
                                    isRequired: checkWatcherFlag(
                                        field.name, EReportPropertiesSettingsFormFields.paramName,
                                        EWatcherFieldFlag.isRequired,
                                    ),
                                },
                            })}
                        >
                            <Input
                                autoComplete="off"
                                placeholder="Введите имя параметра"
                                disabled={checkWatcherFlag(
                                    field.name,
                                    EReportPropertiesSettingsFormFields.paramName,
                                    EWatcherFieldFlag.isDisabled,
                                )}
                            />
                        </Form.Item>
                    )}
                    {!checkWatcherFlag(
                        field.name, EReportPropertiesSettingsFormFields.columnName, EWatcherFieldFlag.isHidden,
                    ) && (
                        <Form.Item
                            style={{width: 187}}
                            label="Имя атрибута"
                            required={checkWatcherFlag(field.name, EReportPropertiesSettingsFormFields.columnName,
                                EWatcherFieldFlag.isRequired)}
                            name={[field.name, EReportPropertiesSettingsFormFields.columnName]}
                            rules={fieldValidationResolver({
                                validationRules: [{
                                    mask: EMaskValidation.checkFieldUniqueness,
                                    errorMessage: 'Такое имя атрибута уже существует',
                                }],
                                settings: {
                                    checkFieldUniquenessArrayPath: REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY,
                                    checkFieldUniquenessFieldKey: EReportPropertiesSettingsFormFields.columnName,
                                },
                                fieldMeta: {
                                    type: EFieldType.REFERENCE,
                                    label: 'Имя атрибута',
                                    isRequired: true,
                                },
                            })}
                        >
                            <CustomSelect
                                entries={tableAttributesEntries}
                                settings={{
                                    placeholder: 'Выберите атрибут',
                                    isDisabled: checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.columnName,
                                        EWatcherFieldFlag.isDisabled,
                                    ),
                                    showSearch: true,
                                }}
                            />
                        </Form.Item>
                    )}
                    <Form.Item
                        required
                        style={{width: 240}}
                        label="Пользовательское наименование"
                        rules={fieldValidationResolver({
                            validationRules: [
                                {
                                    regExp: '^.{1,100}$',
                                    errorMessage: 'Длина данного поля должна быть не более 100 символов',
                                },
                            ],
                            fieldMeta: {
                                type: EFieldType.STRING,
                                label: 'Пользовательское наименование',
                                isRequired: true,
                            },
                        })}
                        name={[field.name, EReportPropertiesSettingsFormFields.reportTitle]}
                    >
                        <Input
                            autoComplete="off"
                            placeholder="Введите наименование"
                            disabled={checkWatcherFlag(
                                field.name,
                                EReportPropertiesSettingsFormFields.reportTitle,
                                EWatcherFieldFlag.isDisabled,
                            )}
                        />
                    </Form.Item>
                    <Form.Item
                        label={<>&nbsp;</>}
                        style={{width: 97}}
                        valuePropName="checked"
                        name={[field.name, EReportPropertiesSettingsFormFields.visible]}
                    >
                        <Checkbox disabled={checkWatcherFlag(
                            field.name,
                            EReportPropertiesSettingsFormFields.visible,
                            EWatcherFieldFlag.isDisabled,
                        )}
                        >
                            Видимый
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        label={<>&nbsp;</>}
                        style={{width: 130}}
                        valuePropName="checked"
                        name={[field.name, EReportPropertiesSettingsFormFields.required]}
                    >
                        <Checkbox disabled={checkWatcherFlag(
                            field.name,
                            EReportPropertiesSettingsFormFields.required,
                            EWatcherFieldFlag.isDisabled,
                        )}
                        >
                            Обязательный
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        style={{width: 220}}
                        label="Тип значения"
                        required
                        rules={fieldValidationResolver({
                            fieldMeta: {
                                type: EFieldType.STRING,
                                label: 'Тип значения',
                                isRequired: true,
                            },
                        })}
                        name={[field.name, EReportPropertiesSettingsFormFields.type]}
                    >
                        <CustomSelect
                            settings={{
                                showSearch: true,
                                url: '/lookupValue/TABLE_REPORT_PARAMETER_TYPE',
                                labelPath: 'meaning',
                                valuePath: 'lookupCode',
                                placeholder: 'Выберите тип',
                                isDisabled: checkWatcherFlag(
                                    field.name,
                                    EReportPropertiesSettingsFormFields.type,
                                    EWatcherFieldFlag.isDisabled,
                                ),
                            }}
                            initialValue={
                                values?.length && values[field.name]?.[EReportPropertiesSettingsFormFields.type]
                            }
                            entriesForLabels={[
                                {
                                    value: "SQLVALUESET",
                                    label: "Динамический набор значений"
                                },
                                {
                                    value: "SQLMULTIVALUESET",
                                    label: "Динамический набор значений с множественным выбором"
                                },
                            ]}
                            handleTriggerEvent={(value, event) => {
                                if (value?.lookupCode) {
                                    setFormatSublistCodes(v => ({
                                        ...v,
                                        [field.name]: FormatLookupSublistCodesMap[
                                            value.lookupCode as keyof typeof FormatLookupSublistCodesMap
                                        ],
                                    }));
                                }
                                if (event === ECustomSelectValueTriggerEvent.valueHasBeenChangedManually) {
                                    resetReportPropertiesSettingsFormFields(
                                        form, field.name,
                                        EReportPropertiesSettingsFormFields.type,
                                    );
                                    applyFieldWatchers(); // чтобы отререндерить форму
                                }
                            }}
                        />
                    </Form.Item>
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultType,
                        EWatcherFieldFlag.isHidden,
                    ) && (
                        <Form.Item
                            style={{width: 270}}
                            label="Тип значения по умолчанию"
                            name={[field.name, EReportPropertiesSettingsFormFields.defaultType]}
                        >
                            <CustomSelect
                                additionalEntries={[{
                                    label: 'Не выбрано', // используется только на фронте как значение по умолчанию
                                    value: {lookupCode: 'UNSET', meaning: 'Не выбрано'},
                                }]}
                                optionsFilterCallback={({entry}) => {
                                    const shouldFilterLookup = checkWatcherFlag(
                                        field.name, EReportPropertiesSettingsFormFields.defaultType,
                                        EWatcherFieldFlag.shouldFilterLookup,
                                    );
                                    return shouldFilterLookup ? entry.value?.lookupCode !== 'CURRENT_DATETIME'
                                        : true;
                                }}
                                settings={{
                                    formInstance: form,
                                    formFieldKey: REPORT_PROPERTIES_SETTINGS_FORM_DATA_KEY,
                                    formFieldPathName: [field.name, EReportPropertiesSettingsFormFields.defaultType],
                                    url: '/lookupValue/TABLE_REPORT_PARAMETER_DEFAULT_TYPE',
                                    labelPath: 'meaning',
                                    valuePath: 'lookupCode',
                                    placeholder: 'Выберите значение по умолчанию',
                                    isDisabled: checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.defaultType,
                                        EWatcherFieldFlag.isDisabled,
                                    ),
                                    useFirstOptionAfterReceivedFromFetcher: true,
                                    noUseFirstOptionWhenValueIsSet: true,
                                    setFirstOptionUntouched: true,
                                }}
                                handleTriggerEvent={(value, event) => {
                                    if (event === ECustomSelectValueTriggerEvent.valueHasBeenChangedManually) {
                                        resetReportPropertiesSettingsFormFields(
                                            form, field.name,
                                            EReportPropertiesSettingsFormFields.defaultType,
                                        );
                                    }
                                }}
                            />
                        </Form.Item>
                    )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.sqlValueset,
                        EWatcherFieldFlag.isHidden,
                    ) && (
                        <Form.Item
                            style={{width: 270}}
                            label="Динамический SQL"
                            rules={[{required: true}]}
                            name={[field.name, EReportPropertiesSettingsFormFields.sqlValueset]}
                        >
                            <LargeStringInput
                                disabled={checkWatcherFlag(field.name,
                                    EReportPropertiesSettingsFormFields.sqlValueset,
                                    EWatcherFieldFlag.isDisabled)}
                                name={EReportPropertiesSettingsFormFields.sqlValueset}
                                label="Динамический SQL"
                                placeholder="Динамический SQL"
                                isDynamicSqlEnabled={isDynamicSqlEnabled}
                            />
                        </Form.Item>
                    )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.valuesetLookupType, EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                required
                                style={{width: 216}}
                                label="Набор значений"
                                rules={fieldValidationResolver({
                                    fieldMeta: {
                                        type: EFieldType.REFERENCE,
                                        label: 'Набор значений',
                                        isRequired: true,
                                    },
                                })}
                                name={[field.name, EReportPropertiesSettingsFormFields.valuesetLookupType]}
                            >
                                <CustomSelect
                                    settings={{
                                        showSearch: true,
                                        url: '/valueLists/LOOKUP_TYPES',
                                        labelPath: 'meaning',
                                        valuePath: 'lookupCode',
                                        placeholder: 'Выберите набор значений',
                                        isDisabled: checkWatcherFlag(
                                            field.name,
                                            EReportPropertiesSettingsFormFields.valuesetLookupType,
                                            EWatcherFieldFlag.isDisabled,
                                        ),
                                    }}
                                    handleTriggerEvent={(value, event) => {
                                        if (value?.lookupCode) {
                                            setSelectedLookupTypes(v => ({...v, [field.name]: value.lookupCode}));
                                        }
                                        if (event === ECustomSelectValueTriggerEvent.valueHasBeenChangedManually) {
                                            resetReportPropertiesSettingsFormFields(
                                                form, field.name,
                                                EReportPropertiesSettingsFormFields.valuesetLookupType,
                                            );
                                        }
                                    }}
                                />
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueSqlValueSet,
                        EWatcherFieldFlag.isHidden,
                    ) && (
                        <Form.Item
                            style={{width: 270}}
                            label="Динамический SQL по умолчанию"
                            name={[field.name, EReportPropertiesSettingsFormFields.defaultValueSqlValueSet]}
                        >
                            <LargeStringInput
                                disabled={checkWatcherFlag(field.name,
                                    EReportPropertiesSettingsFormFields.defaultValueSqlValueSet,
                                    EWatcherFieldFlag.isDisabled)}
                                name={EReportPropertiesSettingsFormFields.defaultValueSqlValueSet}
                                label="Динамический SQL по умолчанию"
                                placeholder="Динамический SQL по умолчанию"
                                isDynamicSqlEnabled={isDynamicSqlEnabled}
                            />
                        </Form.Item>
                    )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueContext,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                style={{width: 270}}
                                label="Значение по умолчанию"
                                name={[field.name, EReportPropertiesSettingsFormFields.defaultValueContext]}
                            >
                                <CustomSelect settings={{
                                    url: '/lookupValue/TABLE_REPORT_PARAMETER_CONTEXT_TYPE',
                                    labelPath: 'meaning',
                                    valuePath: 'lookupCode',
                                    placeholder: 'Выберите значение по умолчанию',
                                    isDisabled: checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.defaultValueContext,
                                        EWatcherFieldFlag.isDisabled,
                                    ),
                                }}
                                />
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueDate,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                style={{width: 270}}
                                label="Значение по умолчанию"
                                name={[field.name, EReportPropertiesSettingsFormFields.defaultValueDate]}
                            >
                                <DateInput
                                    resetTimeOnSet
                                    disabled={checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.defaultValueDate,
                                        EWatcherFieldFlag.isDisabled,
                                    )}
                                />
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueDateTime,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                style={{width: 270}}
                                label="Значение по умолчанию"
                                name={[field.name, EReportPropertiesSettingsFormFields.defaultValueDateTime]}
                            >
                                <DateInput
                                    showTime
                                    disabled={checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.defaultValueDateTime,
                                        EWatcherFieldFlag.isDisabled,
                                    )}
                                />
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueInteger,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                style={{width: 270}}
                                label="Значение по умолчанию"
                                name={[field.name, EReportPropertiesSettingsFormFields.defaultValueInteger]}
                            >
                                <InputNumber
                                    autoComplete="off"
                                    placeholder="Введите значение по умолчанию"
                                    disabled={checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.defaultValueInteger,
                                        EWatcherFieldFlag.isDisabled,
                                    )}
                                />
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueBoolean,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                style={{width: 270}}
                                valuePropName="checked"
                                label={<>&nbsp;</>}
                                name={[field.name, EReportPropertiesSettingsFormFields.defaultValueBoolean]}
                            >
                                <Checkbox
                                    disabled={checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.defaultValueBoolean,
                                        EWatcherFieldFlag.isDisabled,
                                    )}
                                >
                                    Значение по умолчанию
                                </Checkbox>
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueString,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                style={{width: 270}}
                                label="Значение по умолчанию"
                                name={[field.name, EReportPropertiesSettingsFormFields.defaultValueString]}
                            >
                                <Input
                                    autoComplete="off"
                                    placeholder="Введите значение по умолчанию"
                                    disabled={checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.defaultValueString,
                                        EWatcherFieldFlag.isDisabled,
                                    )}
                                />
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueValueSet, EWatcherFieldFlag.isHidden,
                    )
                        && selectedLookupTypes[field.name] && (
                        <Form.Item
                            required
                            style={{width: 270}}
                            label="Значение по умолчанию"
                            name={[field.name, EReportPropertiesSettingsFormFields.defaultValueValueSet]}
                        >
                            <CustomSelect settings={{
                                showSearch: true,
                                url: `/lookupValue/${selectedLookupTypes[field.name]}`,
                                labelPath: 'meaning',
                                valuePath: 'lookupCode',
                                placeholder: 'Выберите значение по умолчанию',
                                isDisabled: checkWatcherFlag(
                                    field.name,
                                    EReportPropertiesSettingsFormFields.defaultValueValueSet,
                                    EWatcherFieldFlag.isDisabled,
                                ),
                            }}
                            />
                        </Form.Item>
                    )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueMultiValueSet, EWatcherFieldFlag.isHidden,
                    )
                        && selectedLookupTypes[field.name] && (
                        <Form.Item
                            required
                            style={{width: 270}}
                            label="Значения по умолчанию"
                            name={[field.name, EReportPropertiesSettingsFormFields.defaultValueMultiValueSet]}
                        >
                            <CustomSelect settings={{
                                showSearch: true,
                                mode: ECustomSelectMode.multiple,
                                url: `/lookupValue/${selectedLookupTypes[field.name]}`,
                                labelPath: 'meaning',
                                valuePath: 'lookupCode',
                                placeholder: 'Выберите значение по умолчанию',
                                isDisabled: checkWatcherFlag(
                                    field.name,
                                    EReportPropertiesSettingsFormFields.defaultValueMultiValueSet,
                                    EWatcherFieldFlag.isDisabled,
                                ),
                            }}
                            />
                        </Form.Item>
                    )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.defaultValueDateRange,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                className={s['date-range-form-item']}
                                label="Значение по умолчанию"
                                name={[field.name, EReportPropertiesSettingsFormFields.defaultValueDateRange]}
                            >
                                <DateRangeInput
                                    dateRangeKeys={{from: 'from', to: 'to'}}
                                    dateRangeKey="dateRange"
                                    disabled={checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.defaultValueDateRange,
                                        EWatcherFieldFlag.isDisabled,
                                    )}
                                />
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.format,
                        EWatcherFieldFlag.isHidden,
                    )
                        && formatSublistCodes[field.name] && (
                        <Form.Item
                            required
                            style={{width: 'fit-content', minWidth: 190}}
                            label="Формат"
                            name={[field.name, EReportPropertiesSettingsFormFields.format]}
                        >
                            <CustomSelect settings={{
                                showSearch: true,
                                url: '/valueLists/FLEX_VALUE'
                                        + `?sublistCode=${formatSublistCodes[field.name]}`,
                                labelPath: 'meaning',
                                valuePath: 'lookupCode',
                                placeholder: 'Выберите формат',
                                isDisabled: checkWatcherFlag(
                                    field.name,
                                    EReportPropertiesSettingsFormFields.format,
                                    EWatcherFieldFlag.isDisabled,
                                ),
                            }}
                            />
                        </Form.Item>
                    )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.inputRestrictions,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                className={s['input-restrictions-form-item']}
                                valuePropName="checked"
                                label={<>&nbsp;</>}
                                name={[field.name, EReportPropertiesSettingsFormFields.inputRestrictions]}
                            >
                                <Checkbox
                                    disabled={checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.inputRestrictions,
                                        EWatcherFieldFlag.isDisabled,
                                    )}
                                >
                                    Ограничения ввода
                                </Checkbox>
                            </Form.Item>
                        )}
                    {!checkWatcherFlag(
                        field.name,
                        EReportPropertiesSettingsFormFields.periodLength,
                        EWatcherFieldFlag.isHidden,
                    )
                        && (
                            <Form.Item
                                className={s['period-length-form-item']}
                                label="Длина периода, дней"
                                name={[field.name, EReportPropertiesSettingsFormFields.periodLength]}
                                required={checkWatcherFlag(field.name, EReportPropertiesSettingsFormFields.periodLength,
                                    EWatcherFieldFlag.isRequired)}
                                rules={fieldValidationResolver({
                                    validationRules: [],
                                    fieldMeta: {
                                        type: EFieldType.NUMBER,
                                        label: 'Длина периода, дней',
                                        isRequired: checkWatcherFlag(
                                            field.name, EReportPropertiesSettingsFormFields.periodLength,
                                            EWatcherFieldFlag.isRequired,
                                        ),
                                    },
                                })}
                            >
                                <InputNumber
                                    autoComplete="off"
                                    placeholder="Введите количество дней"
                                    disabled={checkWatcherFlag(
                                        field.name,
                                        EReportPropertiesSettingsFormFields.periodLength,
                                        EWatcherFieldFlag.isDisabled,
                                    )}
                                />
                            </Form.Item>
                        )}
                    <Form.Item
                        style={{width: 150}}
                        label="Порядковый номер"
                        name={[field.name, EReportPropertiesSettingsFormFields.ordinal]}
                    >
                        <InputNumber
                            autoComplete="off"
                            placeholder="Введите значение"
                            disabled={checkWatcherFlag(
                                field.name,
                                EReportPropertiesSettingsFormFields.ordinal,
                                EWatcherFieldFlag.isDisabled,
                            )}
                        />
                    </Form.Item>
                    <Form.Item label={<>&nbsp;</>}> {/* Для выравнивания при ошибках */}
                        <Button
                            disabled={checkArrayIndependentWatcherFlag('removeButton',
                                EWatcherFieldFlag.isDisabled)}
                            className="button-only-icon"
                            type="primary"
                            icon={<TrashXOutlined />}
                            onClick={() => remove(field.name)}
                        />
                    </Form.Item>
                </div>
            ))}
        </div>
    );
};
